import { useState, useEffect, useRef, useCallback } from "react";

/**
 * Hook to manage page visibility, unread message count in title,
 * and restoring the original title.
 */
export function usePageFocusAndTitle(baseTitle: string = "FST Chat") {
  const [isFocused, setIsFocused] = useState(true); // Assume focused initially
  const [unreadCount, setUnreadCount] = useState(0);
  const originalTitleRef = useRef<string>(document.title || baseTitle); // Store initial/base title

  // Effect to handle visibility change
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        setIsFocused(false);
      } else {
        setIsFocused(true);
        // Reset unread count and title when tab becomes visible again
        setUnreadCount(0);
        document.title = originalTitleRef.current;
      }
    };

    // Store the initial title in case it wasn't set before the effect ran
    originalTitleRef.current = document.title || baseTitle;

    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Initial check in case the page loads hidden
    handleVisibilityChange();

    // Cleanup listener on unmount
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      // Optional: Reset title on unmount? Usually not necessary.
      // document.title = originalTitleRef.current;
    };
  }, [baseTitle]); // Rerun if baseTitle changes (unlikely but possible)

  // Effect to update the title when unread count changes and tab is hidden
  useEffect(() => {
    if (!isFocused && unreadCount > 0) {
      document.title = `(${unreadCount}) ${originalTitleRef.current}`;
    } else {
      // Ensure title is original if focused or count is 0
      document.title = originalTitleRef.current;
    }
    // Dependency on isFocused ensures title resets immediately on focus
  }, [unreadCount, isFocused]);

  // Function to be called when a new message arrives
  const notifyMessageArrived = useCallback(() => {
    if (!document.hidden) {
      // If tab is focused when message arrives, do nothing with the count/title
      return;
    }
    // If hidden, update focus state (in case event listener hasn't fired yet)
    // and increment count
    setIsFocused(false);
    setUnreadCount((count) => count + 1);
  }, []); // No dependencies needed as it reads document.hidden directly

  // Return the notification function and focus state (optional)
  return { isPageFocused: isFocused, notifyMessageArrived };
}
